import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTheme } from './ThemeContext';
import { authLogin, authRegister, authMe } from '../services/api.auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const { applyUserAppearance } = useTheme();

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await authMe();
          setUser(response.data);
          setToken(storedToken);
          applyUserAppearance(response.data.theme_mode, response.data.preferences);
        } catch (error) {
          console.error('Token validation failed:', error);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    validateToken();
  }, [applyUserAppearance]);

  const login = useCallback(async (email, password) => {
    const response = await authLogin(email, password);
    const { access_token, user: userData } = response.data;

    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    applyUserAppearance(userData.theme_mode, userData.preferences);

    return userData;
  }, [applyUserAppearance]);

  const register = useCallback(async (email, password, name) => {
    const response = await authRegister(email, password, name);
    return response.data;
  }, []);

  const logout = useCallback((redirectTo = '/login') => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    // AuthContext sits above BrowserRouter so useNavigate isn't available here.
    // window.location gives us a clean navigation that also resets all React state.
    window.location.href = redirectTo;
  }, []);

  const value = {
    user,
    setUser,
    token,
    isAuthenticated: !!token && !!user,
    setIsAuthenticated: (value) => {
      if (!value) {
        setToken(null);
        setUser(null);
      }
    },
    loading,
    login,
    register,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
